import { useCallback, useState } from 'react';
import {
  LockableScreenOrientation,
  OrientationLockType,
  useIsPortraitOrientation,
  useScreenLockOrientation,
  useShouldForceLandscape,
} from '../../features/quickstart/utils/responsiveHooks';
import { RotationDiagnosticsPanel } from './rotationTest/RotationDiagnosticsPanel';
import { useRotationDiagnostics } from './rotationTest/useRotationDiagnostics';
import { useDeviceTilt } from './rotationTest/useDeviceTilt';

/**
 * Standalone diagnostic harness for the force-vertical rotation pipeline.
 *
 * Reproduces the exact production flow (data-force-landscape CSS rotation +
 * useScreenLockOrientation portrait lock on fullscreen) with a synthetic
 * "screen" that makes the rotation direction unmistakable, and prints every
 * orientation signal live. Built to diagnose the Android 17 wrong-direction
 * fullscreen bug without a live STF session. Route: /rotation-test
 */
export const RotationTest = () => {
  const { snapshot, log, pushEvent, clearLog, copyLog } = useRotationDiagnostics();
  const { requestPermission, ...tilt } = useDeviceTilt();
  const isPortraitOrientation = useIsPortraitOrientation();
  const shouldForceLandscape = useShouldForceLandscape();
  const isVisuallyRotated = shouldForceLandscape && isPortraitOrientation;

  // Same lock the real player applies: resolves to portrait when
  // FORCE_PORTRAIT_ORIENTATION is on, so fullscreen behaves identically.
  useScreenLockOrientation('landscape');

  const [lastLockResult, setLastLockResult] = useState('none');

  const getOrientation = (): LockableScreenOrientation | undefined =>
    window.screen?.orientation as unknown as LockableScreenOrientation | undefined;

  const goFullscreen = useCallback(async () => {
    try {
      await document.documentElement.requestFullscreen({ navigationUI: 'hide' });
    } catch {
      try {
        await document.documentElement.requestFullscreen();
      } catch (error) {
        setLastLockResult(`fullscreen failed: ${(error as Error).name}`);
      }
    }
  }, []);

  const exitFullscreen = useCallback(() => {
    if (document.fullscreenElement) void document.exitFullscreen();
  }, []);

  const doLock = useCallback(
    async (orientation: OrientationLockType) => {
      const screenOrientation = getOrientation();
      if (!screenOrientation?.lock) {
        setLastLockResult('lock() unsupported');
        return;
      }
      try {
        await screenOrientation.lock(orientation);
        setLastLockResult(
          `lock(${orientation}) -> angle ${screenOrientation.angle}, ${screenOrientation.type}`,
        );
        pushEvent(`lock(${orientation}) ok`);
      } catch (error) {
        setLastLockResult(`lock(${orientation}) rejected: ${(error as Error).name}`);
        pushEvent(`lock(${orientation}) rejected`);
      }
    },
    [pushEvent],
  );

  const doUnlock = useCallback(() => {
    const screenOrientation = getOrientation();
    if (!screenOrientation?.unlock) {
      setLastLockResult('unlock() unsupported');
      return;
    }
    try {
      screenOrientation.unlock();
      setLastLockResult('unlocked');
      pushEvent('unlock() ok');
    } catch (error) {
      setLastLockResult(`unlock() rejected: ${(error as Error).name}`);
      pushEvent('unlock() rejected');
    }
  }, [pushEvent]);

  return (
    <div className='relative h-screen w-screen overflow-hidden bg-neutral-900'>
      {/* Synthetic "screen": rotates via the same CSS rule as the real player. */}
      <div
        data-force-landscape={shouldForceLandscape ? 'true' : undefined}
        className='absolute inset-0 flex flex-col items-center justify-between bg-gradient-to-b from-sky-400 to-indigo-900 text-white'
      >
        <div className='w-full py-4 text-center text-2xl font-black tracking-widest'>
          TOP (game up)
        </div>

        <div className='flex flex-1 flex-col items-center justify-center gap-4'>
          <div className='text-[120px] leading-none'>&uarr;</div>
          <div className='text-4xl font-black'>STF</div>
          <div className='text-sm opacity-80'>
            The arrow must point to real-world UP when held in landscape
          </div>
        </div>

        <div className='w-full py-4 text-center text-2xl font-black tracking-widest'>
          BOTTOM
        </div>

        <div className='absolute left-2 top-1/2 -translate-y-1/2 text-lg font-bold'>
          LEFT
        </div>
        <div className='absolute right-2 top-1/2 -translate-y-1/2 text-lg font-bold'>
          RIGHT
        </div>
      </div>

      <RotationDiagnosticsPanel
        snapshot={snapshot}
        hookState={{ isPortraitOrientation, shouldForceLandscape, isVisuallyRotated }}
        tilt={tilt}
        log={log}
        lastLockResult={lastLockResult}
        onCopyLog={copyLog}
        onClearLog={clearLog}
      />

      {/* Controls: fixed + un-rotated so taps map normally. */}
      <div className='pointer-events-auto fixed bottom-0 left-0 right-0 z-[9999] flex flex-wrap items-center justify-center gap-2 bg-black/85 p-2 font-mono text-xs'>
        <button
          type='button'
          onClick={goFullscreen}
          className='rounded bg-green-600 px-3 py-2 font-bold text-white hover:bg-green-500'
        >
          Go Fullscreen
        </button>
        <button
          type='button'
          onClick={exitFullscreen}
          className='rounded bg-red-600 px-3 py-2 font-bold text-white hover:bg-red-500'
        >
          Exit
        </button>
        <button
          type='button'
          onClick={() => doLock('portrait')}
          className='rounded bg-purple-700 px-2 py-2 font-semibold text-white hover:bg-purple-600'
        >
          lock portrait
        </button>
        <button
          type='button'
          onClick={() => doLock('portrait-primary')}
          className='rounded bg-purple-700 px-2 py-2 font-semibold text-white hover:bg-purple-600'
        >
          lock portrait-primary
        </button>
        <button
          type='button'
          onClick={() => doLock('portrait-secondary')}
          className='rounded bg-purple-700 px-2 py-2 font-semibold text-white hover:bg-purple-600'
        >
          lock portrait-secondary
        </button>
        <button
          type='button'
          onClick={() => doLock('landscape')}
          className='rounded bg-purple-700 px-2 py-2 font-semibold text-white hover:bg-purple-600'
        >
          lock landscape
        </button>
        <button
          type='button'
          onClick={doUnlock}
          className='rounded bg-gray-700 px-2 py-2 font-semibold text-white hover:bg-gray-600'
        >
          unlock
        </button>
        <button
          type='button'
          onClick={() => void requestPermission()}
          className='rounded bg-pink-700 px-2 py-2 font-semibold text-white hover:bg-pink-600'
        >
          Enable motion
        </button>
      </div>
    </div>
  );
};

export default RotationTest;
